import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DashboardSkeleton } from "@/components/ui/page-skeletons";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { PanelHeading, PanelNavLink } from "@/components/shell/SectionPanel";
import { ShellPanel } from "@/components/shell/ShellContext";
import { AgendaTile, ApprovalsTile, AttentionTile, ChartTile, ConversionFunnelTile, ForecastTile, FunnelTile, HotTile, KpiStrip, LeaderTile, TargetTile } from "./tiles";
import { usePins } from "@/lib/usePins";
import { monthRange, useManagerHome } from "./useManagerHome";

/**
 * Home for Sales Heads and Super Admins: one overview of the team's numbers, built as a bento of tiles.
 * (Reps land on My Day instead.)
 */
const TILES: { key: string; label: string; headOnly?: boolean }[] = [
  { key: "kpis", label: "Key figures" },
  { key: "forecast", label: "Forecast" },
  { key: "target", label: "Target" },
  { key: "funnel", label: "Funnel" },
  { key: "leaders", label: "Leaderboard" },
  { key: "attention", label: "Needs attention" },
  { key: "approvals", label: "Approvals", headOnly: true },
  { key: "hot", label: "Hot right now" },
  { key: "agenda", label: "Today" },
];

/** Which tiles this person hides on their Home, remembered in this browser */
function useHiddenTiles() {
  const KEY = "crm.home.hiddenTiles";
  const [hidden, setHidden] = useState<string[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || "[]");
      return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(hidden));
    } catch {
      // Not critical
    }
  }, [hidden]);
  return { hidden, toggle: (k: string) => setHidden((h) => (h.includes(k) ? h.filter((x) => x !== k) : [...h, k])), reset: () => setHidden([]) };
}

export function BentoHome() {
  const tiles = useHiddenTiles();
  const [editing, setEditing] = useState(false);
  const show = (k: string) => !tiles.hidden.includes(k);
  const { user, hasPermission } = useAuth();
  const { data, loading, error, reload } = useManagerHome();
  const { daysLeft, monthName } = monthRange();
  const isHead = data?.role === "SALES_HEAD";
  const { pins } = usePins();

  const panel = (
    <ShellPanel title="Home" subtitle={isHead ? "Your team" : "The whole company"}>
      <div className="flex flex-col gap-0.5">
        <PanelNavLink to="/dashboard">Overview</PanelNavLink>
        <PanelNavLink to="/dashboard/my-day">My Day</PanelNavLink>
        {hasPermission("activity.read") && <PanelNavLink to="/tasks">Tasks</PanelNavLink>}
        {(hasPermission("calendar.read") || hasPermission("activity.read")) && <PanelNavLink to="/calendar">Calendar</PanelNavLink>}
      </div>
      <PanelHeading>Insights</PanelHeading>
      <div className="flex flex-col gap-0.5">
        {hasPermission("report.view_own") && <PanelNavLink to="/reports">Reports</PanelNavLink>}
        {hasPermission("report.view_own") && <PanelNavLink to="/reports?tab=forecast">Forecast</PanelNavLink>}
        {hasPermission("report.view_own") && <PanelNavLink to="/reports?tab=execs">Team performance</PanelNavLink>}
        {hasPermission("report.view_own") && <PanelNavLink to="/reports?tab=sources">Lead sources</PanelNavLink>}
        {hasPermission("target.read") && <PanelNavLink to="/targets">Targets</PanelNavLink>}
      </div>
      {pins.length > 0 && (
        <>
          <PanelHeading>Pinned</PanelHeading>
          <div className="flex flex-col gap-0.5">
            {pins.map((p) => (
              <PanelNavLink key={p.id} to={p.to}>
                {p.label}
              </PanelNavLink>
            ))}
          </div>
        </>
      )}
    </ShellPanel>
  );

  if (loading && !data) return <>{panel}<DashboardSkeleton /></>;

  if (error || !data) {
    return (
      <>
        {panel}
        <div className="rounded-2xl border border-border bg-card p-10 text-center">
          <p className="text-sm text-muted-foreground">{error || "Could not load the overview."}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={reload}>
            Try again
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-3">
      {panel}
      <div className="flex flex-wrap items-end justify-between gap-3 pb-1">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            Overview <span className="text-[15px] font-normal text-muted-foreground">· {monthName}</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            {isHead ? "How your team is doing, and who needs help." : "How the whole company is doing this month."} Welcome back, {user?.FirstName || "there"}.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditing((e) => !e)} aria-expanded={editing}>
            {editing ? "Done" : "Edit tiles"}
          </Button>
          <Button variant="outline" size="sm" onClick={reload} disabled={loading} className="gap-1.5">
            <RefreshCw className={cn("size-3.5", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {editing && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border bg-card px-4 py-2.5 text-[13px]">
          <span className="font-semibold">Show on your Home:</span>
          {TILES.filter((t) => !t.headOnly || isHead).map((t) => (
            <label key={t.key} className="flex cursor-pointer items-center gap-1.5">
              <input type="checkbox" className="size-3.5 accent-primary" checked={show(t.key)} onChange={() => tiles.toggle(t.key)} />
              {t.label}
            </label>
          ))}
          {tiles.hidden.length > 0 && (
            <button type="button" onClick={tiles.reset} className="ml-auto text-xs text-primary hover:underline">
              Show everything
            </button>
          )}
        </div>
      )}

      {show("kpis") && <KpiStrip k={data.kpis} awaiting={data.quotesAwaiting} />}

      <div className="grid grid-cols-1 gap-3 @4xl/main:grid-cols-12">
        {show("forecast") && (data.forecast ? <ForecastTile forecast={data.forecast} className="@4xl/main:col-span-8" /> : <ChartTile chart={data.chart} className="@4xl/main:col-span-8" />)}
        {show("target") && <TargetTile k={data.kpis} daysLeft={daysLeft} monthName={monthName} className="@4xl/main:col-span-4" />}

        {show("funnel") && (data.funnel ? <ConversionFunnelTile funnel={data.funnel} className="@4xl/main:col-span-4" /> : <FunnelTile stages={data.stages} className="@4xl/main:col-span-4" />)}
        {show("leaders") && <LeaderTile title={data.leadersTitle} leaders={data.leaders} className="@4xl/main:col-span-4" />}
        {show("attention") && <AttentionTile rows={data.attention} className="@4xl/main:col-span-4" />}

        {isHead && show("approvals") ? <ApprovalsTile rows={data.approvals} className="@4xl/main:col-span-6" /> : null}
        {show("hot") && <HotTile rows={data.hot} className={isHead && show("approvals") ? "@4xl/main:col-span-6" : "@4xl/main:col-span-12"} />}

        {show("agenda") && <AgendaTile tasks={data.agenda} className="@4xl/main:col-span-12" />}
      </div>
    </div>
  );
}
